import apiClient from '../lib/apiClient';

// Batch update attendance records
const batchUpdate = async (attendanceIds, updates) => {
  try {
    const result = await apiClient.post('/attendance-batch/batchUpdate', {
      attendance_ids: attendanceIds,
      updates: updates
    });
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to batch update attendance' };
  }
};

// Batch approve regularization requests
const batchApprove = async (requestIds) => {
  try {
    const result = await apiClient.post('/attendance-batch/batchApprove', {
      request_ids: requestIds
    });
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to batch approve requests' };
  }
};

// Batch delete attendance records
const batchDelete = async (attendanceIds) => {
  try {
    const result = await apiClient.post('/attendance-batch/batchDelete', {
      attendance_ids: attendanceIds
    });
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to batch delete attendance' };
  }
};

// Batch export attendance records
const batchExport = async (attendanceIds, format = 'csv') => {
  try {
    const ids = Array.isArray(attendanceIds) ? attendanceIds.join(',') : attendanceIds;
    const queryParams = new URLSearchParams({
      attendance_ids: ids,
      format: format
    });

    const baseURL = import.meta.env.VITE_API_BASE_URL ||
      import.meta.env.VITE_ERP_API_URL ||
      (window.location.hostname === 'localhost' ? `http://${window.location.hostname}:8000/api` : window.location.origin + '/api');
    const url = `${baseURL}/attendance-batch/batchExport?${queryParams}`;

    const token = localStorage.getItem('erp_auth_token');
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    const extension = format === 'excel' ? 'xls' : 'csv';
    link.download = `attendance_batch_export_${new Date().toISOString().split('T')[0]}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message || 'Failed to export attendance' };
  }
};

// Batch create attendance records
const batchCreate = async (data) => {
  try {
    const result = await apiClient.post('/attendance-batch/batchCreate', data);
    return result;
  } catch (error) {
    return { success: false, error: error.message || 'Failed to batch create attendance' };
  }
};

// Save monthly attendance — uses saveMonthly when available, else batchCreate per day
const saveMonthly = async ({ employee_id, records, entry_method = 'manual' }) => {
  const isMissingEndpoint = (errorMessage = '') => {
    const msg = String(errorMessage).toLowerCase();
    return msg.includes('not found') || msg.includes('action not found');
  };

  try {
    const result = await apiClient.post('/attendance-batch/saveMonthly', {
      employee_id,
      records,
      entry_method
    });
    return result;
  } catch (error) {
    if (!isMissingEndpoint(error.message)) {
      return { success: false, error: error.message || 'Failed to save monthly attendance' };
    }
  }

  let successCount = 0;
  let failedCount = 0;

  for (const record of records) {
    const dayResult = await batchCreate({
      employee_ids: [employee_id],
      attendance_date: record.attendance_date,
      clock_in_time: record.clock_in_time,
      clock_out_time: record.clock_out_time,
      status: record.status || 'present',
      notes: record.notes || '',
      entry_method
    });

    if (dayResult.success && (dayResult.data?.success_count ?? 0) > 0) {
      successCount += dayResult.data.success_count;
    } else {
      failedCount += 1;
    }
  }

  if (successCount === 0 && failedCount > 0) {
    return {
      success: false,
      error: 'Failed to save monthly attendance. Please try again or contact support.'
    };
  }

  return {
    success: true,
    data: {
      success_count: successCount,
      failed_count: failedCount,
      total: records.length
    }
  };
};

export default {
  batchUpdate,
  batchApprove,
  batchDelete,
  batchExport,
  batchCreate,
  saveMonthly
};


